<script setup lang="ts">
import { computed } from 'vue';
import type { Props, Slots } from './types';

const props = withDefaults(defineProps<Props>(), {
	color: 'blue',
	styling: '',
	size: 's',
});

defineSlots<Slots>();

const tagName = computed(() => props.href ? 'a' : 'button');

const type = computed(() => props.isSubmit ? 'submit' : undefined);
</script>

<template>
	<component
		:is="tagName"
		:class="{
			['top-active']: isActive,
			['top-disabled']: disabled,
			['top-forms-focusable']: !disabled,
			['top-button']: true,
			['top-button-progress']: isProgress,
			[`top-size_${size}`]: !!size,
			[`top-color_${color}`]: true,
			[`top-style_${styling}`]: !!styling,
			['top-button-autoWidth']: autoWidth,
			['top-button-withoutText']: !$slots.default,
		}"
		:name="name"
		:title="title"
		:href="href"
		:type="type"
		:data-top-icon="icon || undefined"
		:data-top-icon2="icon2 || undefined"
		:disabled="disabled || undefined"
		:inProgress="isProgress"
	>
		<span
			v-if="badgeNumber"
			:data-top-badge="badgeNumber"
			class="top-button_badge"
			:class="{
				['top-button_badge-afterText']: badgePosAfterText,
			}"
		></span>

		<span
			v-if="$slots.default"
			class="top-ellipsis"
		>
			<slot>
				{{ !icon ? 'Button' : '' }}
			</slot>
		</span>

		<slot name="html"></slot>
	</component>
</template>

<style>
@import "./style/button.css";
@import "./style/style-outline.css";
@import "./style/style-soft.css";
@import "./style/style-transparent.css";

.top-button {
	--top-button-color: var(--color-white);
	--top-button-background-color: transparent;
	--top-button-background-color-hover: var(--top-button-background-color);
	--top-button-background-color-active: var(--top-button-background-color-hover);
	--top-button-background-color-selected: var(--top-button-background-color-hover);
	--top-button-box-shadow: none;
	--top-button-box-shadow-hover: var(--top-shadow-darken-2);
	--top-button-box-shadow-active: var(--top-shadow-darken-3);
	--top-button-box-shadow-selected: var(--top-shadow-darken-3);
	--top-forms-border-width: 0px;
	--top-icon-width: calc(var(--top-icon-size) + var(--top-forms-padding));
	--top-icon2-width: calc(var(--top-icon2-size) + var(--top-forms-padding));
}

/* Для упрощения работы с event target */
.top-button > span { pointer-events: none; }

.top-button > .top-ellipsis { flex-grow: 0; }

.top-button.top-size_xs {
	--top-forms-padding: var(--top-forms-padding_xs);
	--top-forms-base-height: var(--top-forms-base-height_xs);
}

.top-button.top-size_m {
	--top-forms-padding: var(--top-forms-padding_m);
	--top-forms-base-height: var(--top-forms-base-height_m);
}

.top-button.top-size_l {
	--top-forms-padding: var(--top-forms-padding_l);
	--top-forms-base-height: var(--top-forms-base-height_l);
}

.top-button_badge-afterText {
	order: 100;
}

.top-button:not([data-top-icon2]) > .top-button_badge-afterText {
	margin-left: 0;
}
</style>
